import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { ROLES_TO_DELETE_TEAM } from '@pletly/shared';
import { Toast, showApolloErrorToUser, useConfirm } from '@pletly/ui';

import { GetCurrentUserOrganisationInfoDocument } from '../../../../context/OrganisationInfo/__generated__/organisationInfo.generated';
import { GetOrganisationDocument } from '../../../view/components/__generated__/organisation.generated';
import { useDeleteOrganisationTeamMemberMutation } from './__generated__/deleteTeam.mutation.generated';
import { useOrganisationInfo } from '../../../../context';
import { Team } from '../../components/useTeamData';

import { OrganisationsStackParamsList } from '../../../../navigation';

export const useDeleteTeam = ({
	uuid,
	organisationUuid,
	team,
}: {
	uuid: string;
	organisationUuid: string;
	team: Team | null;
}) => {
	const { t } = useTranslation();
	const { replace } = useNavigation<NativeStackNavigationProp<OrganisationsStackParamsList>>();
	const { confirm } = useConfirm();

	const [deleteTeam] = useDeleteOrganisationTeamMemberMutation({
		refetchQueries: [GetCurrentUserOrganisationInfoDocument, GetOrganisationDocument],
	});

	const { organisationInfo } = useOrganisationInfo();

	const teamName = team?.name ?? 'this';
	const hasAnySubTeams = !!team?.subTeams.length;
	const hasAnyMembers = !!team?.members.caregivers.length || !!team?.members.careRecipients.length;
	const canDelete = !!organisationInfo && ROLES_TO_DELETE_TEAM.includes(organisationInfo.role);
	const handleDeletePress = useCallback(async () => {
		const confirmMessage = `${
			hasAnySubTeams ? t('teams.delete.this-will-also-remove-its-sub-teams-if-any') : ''
		} ${
			hasAnyMembers
				? t(
						'teams.delete.if-you-want-also-to-disconnect-caregivers-or-care-recipients-inside-team-please-do-so-first',
					)
				: ''
		} `;
		const isConfirmed = await confirm(
			t('teams.delete.are-you-sure-you-want-to-delete-the-name-team', { name: teamName }),
			confirmMessage,
		);
		if (isConfirmed === false) {
			return;
		}

		try {
			await deleteTeam({
				variables: {
					data: { teamUuid: uuid },
				},
			});
			replace('OrganisationView', {
				uuid: organisationUuid,
			});

			Toast.show({
				type: 'success',
				text1: t('teams.delete.team-deleted'),
			});
		} catch (error) {
			showApolloErrorToUser(error as unknown as Error);
		}
	}, [
		hasAnySubTeams,
		t,
		hasAnyMembers,
		confirm,
		teamName,
		deleteTeam,
		uuid,
		replace,
		organisationUuid,
	]);

	return {
		handleDeletePress,
		canDelete,
	};
};
